import { CheckCircle2, AlertTriangle, XCircle, Clock, Hourglass, Mail, Award, MinusCircle } from "lucide-react";

// Decision states drive every recommendation/outcome badge on the platform.
//
// Two "layers" coexist:
//   • NTN Recommendation (set by NTN staff): approve | conditional | deny
//   • PM Final Decision   (set by Property Manager — closes the application):
//       final_approved | final_denied | final_not_selected
//
// Final Decision ALWAYS wins over Recommendation visually because it is the
// closing outcome and routes the application to the Closed Applications Archive.
const STYLES = {
  approve:    { bg: "bg-[#22C55E]/12",   text: "text-[#15803D]", ring: "ring-[#22C55E]/30",  Icon: CheckCircle2,   label: "RECOMMENDED" },
  conditional:{ bg: "bg-[#F59E0B]/12",   text: "text-[#92400E]", ring: "ring-[#F59E0B]/30",  Icon: AlertTriangle,  label: "RECOMMENDED WITH CONDITIONS" },
  deny:       { bg: "bg-[#EF4444]/12",   text: "text-[#B91C1C]", ring: "ring-[#EF4444]/30",  Icon: XCircle,        label: "NOT RECOMMENDED" },
  ready:      { bg: "bg-[#0A192F]/8",    text: "text-[#0A192F]", ring: "ring-[#0A192F]/15",  Icon: CheckCircle2,   label: "REPORTS READY" },
  in_progress:{ bg: "bg-[#F59E0B]/10",   text: "text-[#92400E]", ring: "ring-[#F59E0B]/25",  Icon: Hourglass,      label: "IN PROGRESS" },
  awaiting:   { bg: "bg-[#6366F1]/12",   text: "text-[#4338CA]", ring: "ring-[#6366F1]/25",  Icon: Mail,           label: "AWAITING APPLICANT" },
  submitted:  { bg: "bg-[#0A192F]/5",    text: "text-[#4A5568]", ring: "ring-[#0A192F]/10",  Icon: Clock,          label: "SUBMITTED" },
  // PM Final Decision states
  final_approved:     { bg: "bg-emerald-100",  text: "text-emerald-800", ring: "ring-emerald-300",  Icon: Award,        label: "APPROVED" },
  final_denied:       { bg: "bg-rose-100",     text: "text-rose-800",    ring: "ring-rose-300",     Icon: XCircle,      label: "NOT APPROVED" },
  final_not_selected: { bg: "bg-slate-100",    text: "text-slate-700",   ring: "ring-slate-300",    Icon: MinusCircle,  label: "NOT SELECTED" },
};

const FINAL_DECISION_KEY = {
  approved:     "final_approved",
  denied:       "final_denied",
  not_selected: "final_not_selected",
};

// Human-readable labels for raw final_decision values. Exported so other
// surfaces (filter chips, archive tables, executive stripe) use the exact
// platform terminology — "Approved" / "Not Approved" / "Not Selected".
export const FINAL_DECISION_LABEL = {
  approved:     "Approved",
  denied:       "Not Approved",
  not_selected: "Not Selected",
};

export function decisionState(s) {
  // Final Decision is the closing outcome and supersedes any recommendation.
  if (s.final_decision && FINAL_DECISION_KEY[s.final_decision]) {
    return FINAL_DECISION_KEY[s.final_decision];
  }
  if (s.recommendation) return s.recommendation;
  // v1.0.7 — prefer the server-computed canonical workflow stage; the
  // persisted overall_status is only a legacy fallback.
  const vs = s.workflow_stage || s.verify_status;
  if (vs === "ready_for_processing" || vs === "complete") return "ready";
  if (["verification_in_progress", "validation_in_progress",
       "additional_information_requested"].includes(vs)) return "in_progress";
  if (s.application_status === "sent" && s.overall_status === "submitted") return "awaiting";
  if (!vs && s.overall_status === "complete") return "ready";
  if (!vs && s.overall_status === "in_progress") return "in_progress";
  return "submitted";
}

export default function DecisionBadge({ state, size = "md", pulse = false, className = "" }) {
  const cfg = STYLES[state] || STYLES.submitted;
  const sizes = {
    sm: "text-[9px] px-1.5 py-0.5 gap-1 rounded-sm",
    md: "text-[10px] px-2 py-1 gap-1.5 rounded-sm",
    lg: "text-xs px-3 py-1.5 gap-2 rounded-sm",
  };
  return (
    <span className={`inline-flex items-center font-mono font-bold tracking-[0.12em] ring-1 ring-inset ${cfg.bg} ${cfg.text} ${cfg.ring} ${sizes[size]} ${pulse && state === "awaiting" ? "ntn-pulse" : ""} ${className}`}
      data-testid={`decision-${state}`}>
      <cfg.Icon className={size === "sm" ? "h-2.5 w-2.5" : size === "lg" ? "h-4 w-4" : "h-3 w-3"} strokeWidth={2.5} />
      {cfg.label}
    </span>
  );
}
